import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calculator, DollarSign, Save, Settings, TrendingUp, Loader2, Zap, Moon, Calendar } from 'lucide-react';
import { pricingAPI } from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export default function PricingCalculator() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') === 'settings' ? 'settings' : 'calculator');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const [calcForm, setCalcForm] = useState({
    key_type: 'standard',
    labor_minutes: 30,
    is_emergency: false,
    is_after_hours: false,
    is_weekend: false,
    is_holiday: false,
    include_travel: true,
    custom_parts_cost: 0,
  });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await pricingAPI.getSettings();
      setSettings(data);
    } catch (e) { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const calculate = async () => {
    setCalculating(true);
    try {
      const data = await pricingAPI.calculate(calcForm);
      setResult(data);
    } catch (e) { toast.error('Failed to calculate'); }
    finally { setCalculating(false); }
  };

  useEffect(() => {
    if (settings) calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcForm, settings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await pricingAPI.updateSettings(settings);
      toast.success('Settings saved!');
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="pricing-calculator-page">
      <div>
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
          <Calculator className="w-8 h-8 text-primary" />
          Pricing Calculator
        </h1>
        <p className="text-muted-foreground">Auto-calculate job pricing with modifiers</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="calculator"><Calculator className="w-4 h-4 mr-2" />Calculator</TabsTrigger>
          <TabsTrigger value="settings"><Settings className="w-4 h-4 mr-2" />Settings</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'calculator' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Input Form */}
          <Card>
            <CardContent className="p-5 space-y-4">
              <h3 className="font-bold text-lg">Job Details</h3>
              <div className="space-y-2">
                <Label>Key Type</Label>
                <Select value={calcForm.key_type} onValueChange={(v) => setCalcForm({ ...calcForm, key_type: v })}>
                  <SelectTrigger data-testid="key-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Key (${settings?.key_cost_standard})</SelectItem>
                    <SelectItem value="transponder">Transponder Key (${settings?.key_cost_transponder})</SelectItem>
                    <SelectItem value="flip">Flip Key (${settings?.key_cost_flip})</SelectItem>
                    <SelectItem value="smart">Smart Key (${settings?.key_cost_smart})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Labor Time (minutes)</Label>
                <Input type="number" min="0" value={calcForm.labor_minutes} onChange={(e) => setCalcForm({ ...calcForm, labor_minutes: parseInt(e.target.value) || 0 })} data-testid="labor-minutes" />
              </div>
              <div className="space-y-2">
                <Label>Custom Parts Cost ($)</Label>
                <Input type="number" step="0.01" min="0" value={calcForm.custom_parts_cost} onChange={(e) => setCalcForm({ ...calcForm, custom_parts_cost: parseFloat(e.target.value) || 0 })} data-testid="custom-parts-cost" />
              </div>
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2"><Zap className="w-4 h-4 text-red-500" />Emergency Call</Label>
                  <Switch data-testid="toggle-emergency" checked={calcForm.is_emergency} onCheckedChange={(v) => setCalcForm({ ...calcForm, is_emergency: v })} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2"><Moon className="w-4 h-4 text-purple-500" />After Hours (1.5x)</Label>
                  <Switch data-testid="toggle-after-hours" checked={calcForm.is_after_hours} onCheckedChange={(v) => setCalcForm({ ...calcForm, is_after_hours: v, is_weekend: false, is_holiday: false })} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2"><Calendar className="w-4 h-4 text-blue-500" />Weekend (1.25x)</Label>
                  <Switch data-testid="toggle-weekend" checked={calcForm.is_weekend} onCheckedChange={(v) => setCalcForm({ ...calcForm, is_weekend: v, is_after_hours: false, is_holiday: false })} />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2"><Calendar className="w-4 h-4 text-red-500" />Holiday (2x)</Label>
                  <Switch data-testid="toggle-holiday" checked={calcForm.is_holiday} onCheckedChange={(v) => setCalcForm({ ...calcForm, is_holiday: v, is_after_hours: false, is_weekend: false })} />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Include Travel Fee</Label>
                  <Switch data-testid="toggle-travel" checked={calcForm.include_travel} onCheckedChange={(v) => setCalcForm({ ...calcForm, include_travel: v })} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Result */}
          <div className="space-y-3">
            {result && (
              <>
                <Card className="border-green-500/30 bg-gradient-to-br from-green-500/10 to-emerald-500/5">
                  <CardContent className="p-5 text-center">
                    <p className="text-sm text-muted-foreground mb-1">Suggested Customer Price</p>
                    <p className="text-5xl font-bold text-green-500" data-testid="suggested-price-total">{formatCurrency(result.final_price)}</p>
                    <p className="text-sm text-muted-foreground mt-2">Profit: <span className="text-green-500 font-semibold">{formatCurrency(result.profit)}</span></p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-5">
                    <h4 className="font-bold mb-3">Breakdown</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Key Cost ({result.key_type})</span><span className="font-mono">{formatCurrency(result.key_cost)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Labor ({result.labor_minutes} min)</span><span className="font-mono">{formatCurrency(result.labor_cost)}</span></div>
                      {result.travel_fee > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Travel Fee</span><span className="font-mono">{formatCurrency(result.travel_fee)}</span></div>}
                      {result.emergency_fee > 0 && <div className="flex justify-between text-red-500"><span>Emergency Fee</span><span className="font-mono">{formatCurrency(result.emergency_fee)}</span></div>}
                      {result.parts_cost > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Parts</span><span className="font-mono">{formatCurrency(result.parts_cost)}</span></div>}
                      <div className="border-t pt-2 flex justify-between font-semibold"><span>Base Cost</span><span className="font-mono">{formatCurrency(result.base_cost)}</span></div>
                      <div className="flex justify-between text-green-500"><span>+ Profit Margin ({result.profit_margin_percent}%)</span><span className="font-mono">{formatCurrency(result.suggested_price - result.base_cost)}</span></div>
                      {result.tax_amount > 0 && <div className="flex justify-between text-blue-500"><span>+ Tax ({result.tax_rate_percent}%)</span><span className="font-mono">{formatCurrency(result.tax_amount)}</span></div>}
                      <div className="border-t pt-2 flex justify-between font-bold text-lg"><span>Total</span><span className="font-mono">{formatCurrency(result.final_price)}</span></div>
                    </div>
                    {(result.modifiers.after_hours || result.modifiers.weekend || result.modifiers.holiday) && (
                      <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs">
                        <p className="text-amber-500 font-semibold">⏰ Modifier applied:</p>
                        {result.modifiers.after_hours && <p>After hours pricing</p>}
                        {result.modifiers.weekend && <p>Weekend pricing</p>}
                        {result.modifiers.holiday && <p>Holiday pricing</p>}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'settings' && settings && (
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg">Pricing Settings</h3>
              <Button onClick={saveSettings} className="btn-gradient-success" disabled={saving} data-testid="save-settings">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save</>}
              </Button>
            </div>

            <div>
              <h4 className="font-semibold mb-2 text-sm">Key Costs</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Standard Key</Label><Input type="number" step="0.01" value={settings.key_cost_standard} onChange={(e) => setSettings({ ...settings, key_cost_standard: parseFloat(e.target.value) || 0 })} /></div>
                <div className="space-y-1"><Label className="text-xs">Transponder</Label><Input type="number" step="0.01" value={settings.key_cost_transponder} onChange={(e) => setSettings({ ...settings, key_cost_transponder: parseFloat(e.target.value) || 0 })} /></div>
                <div className="space-y-1"><Label className="text-xs">Flip Key</Label><Input type="number" step="0.01" value={settings.key_cost_flip} onChange={(e) => setSettings({ ...settings, key_cost_flip: parseFloat(e.target.value) || 0 })} /></div>
                <div className="space-y-1"><Label className="text-xs">Smart Key</Label><Input type="number" step="0.01" value={settings.key_cost_smart} onChange={(e) => setSettings({ ...settings, key_cost_smart: parseFloat(e.target.value) || 0 })} /></div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2 text-sm">Fees</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Labor Rate/hr</Label><Input type="number" step="0.01" value={settings.labor_rate_per_hour} onChange={(e) => setSettings({ ...settings, labor_rate_per_hour: parseFloat(e.target.value) || 0 })} /></div>
                <div className="space-y-1"><Label className="text-xs">Travel Fee</Label><Input type="number" step="0.01" value={settings.travel_fee} onChange={(e) => setSettings({ ...settings, travel_fee: parseFloat(e.target.value) || 0 })} /></div>
                <div className="space-y-1"><Label className="text-xs">Emergency Fee</Label><Input type="number" step="0.01" value={settings.emergency_fee} onChange={(e) => setSettings({ ...settings, emergency_fee: parseFloat(e.target.value) || 0 })} /></div>
                <div className="space-y-1"><Label className="text-xs">Profit Margin (%)</Label><Input type="number" step="0.1" value={settings.profit_margin_percent} onChange={(e) => setSettings({ ...settings, profit_margin_percent: parseFloat(e.target.value) || 0 })} /></div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2 text-sm">Multipliers</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label className="text-xs">After Hours</Label><Input type="number" step="0.05" value={settings.after_hours_multiplier} onChange={(e) => setSettings({ ...settings, after_hours_multiplier: parseFloat(e.target.value) || 1 })} /></div>
                <div className="space-y-1"><Label className="text-xs">Weekend</Label><Input type="number" step="0.05" value={settings.weekend_multiplier} onChange={(e) => setSettings({ ...settings, weekend_multiplier: parseFloat(e.target.value) || 1 })} /></div>
                <div className="space-y-1"><Label className="text-xs">Holiday</Label><Input type="number" step="0.05" value={settings.holiday_multiplier} onChange={(e) => setSettings({ ...settings, holiday_multiplier: parseFloat(e.target.value) || 1 })} /></div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
